import React, { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.css';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { koKR } from '@mui/material/locale';
import '../../scss/app.scss';
import Router from './Router';
import store, { type RootState } from './store';
import ScrollToTop from './ScrollToTop';

function AdminTheme({ children }: PropsWithChildren) {
  const themeName = useSelector((state: RootState) => state.theme.className);
  const theme = useMemo(() => createTheme({
    palette: {
      mode: themeName === 'theme-dark' ? 'dark' : 'light',
    },
  }, koKR), [themeName]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

export default function App() {
  const [loading, setLoading] = useState(document.readyState !== 'complete');
  const [loaded, setLoaded] = useState(document.readyState === 'complete');

  useEffect(() => {
    let animationTimer: number | undefined;
    const finishLoading = () => {
      setLoading(false);
      animationTimer = window.setTimeout(() => setLoaded(true), 500);
    };

    if (document.readyState === 'complete') finishLoading();
    else window.addEventListener('load', finishLoading, { once: true });

    return () => {
      window.removeEventListener('load', finishLoading);
      if (animationTimer !== undefined) window.clearTimeout(animationTimer);
    };
  }, []);

  return (
    <Provider store={store}>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop>
          {!loaded && (
            <div className={`load${loading ? '' : ' loaded'}`} aria-label="관리자 도구 로딩 중">
              <div className="load__icon-wrap">
                <svg className="load__icon" aria-hidden="true">
                  <path fill="#3553a0" d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z" />
                </svg>
              </div>
            </div>
          )}
          <AdminTheme>
            <Router />
          </AdminTheme>
        </ScrollToTop>
      </BrowserRouter>
    </Provider>
  );
}
